import { registrazioneSchema } from '@aulaviva/shared';
import { Alert, Button, CodiceFiscaleField, PageHeading, TextField } from '@aulaviva/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { z } from 'zod';
import { useRegistrazione } from '@/api/auth';
import { ErroreRichiesta } from '@/api/client';
import { usePercorsoPubblico } from '@/api/percorsi';
import { auth, percorsi } from '@/testi';

type Input = z.input<typeof registrazioneSchema>;
type Output = z.output<typeof registrazioneSchema>;

export function Registrati() {
  const t = auth.registrati;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const percorsoId = params.get('percorso');
  const percorsoPubblico = usePercorsoPubblico(percorsoId);
  const registrazione = useRegistrazione();
  const [errore, setErrore] = useState<string | null>(null);
  const { register, control, handleSubmit, setError, formState } = useForm<Input, unknown, Output>({
    resolver: zodResolver(registrazioneSchema),
    defaultValues: { codiceFiscale: '', nome: '', cognome: '', email: '', ordine: '', numeroAlbo: '' },
  });

  const iscrizioneAperta = percorsoPubblico.data?.iscrizioneAperta ?? false;

  async function invia(dati: Output) {
    setErrore(null);
    try {
      const { email } = await registrazione.mutateAsync({ ...dati, ...(percorsoId && iscrizioneAperta ? { percorsoId } : {}) });
      navigate(`/verifica-email?email=${encodeURIComponent(email)}`);
    } catch (e) {
      if (e instanceof ErroreRichiesta) {
        for (const [campo, messaggi] of Object.entries(e.dettagli ?? {})) {
          setError(campo as keyof Input, { message: messaggi[0] });
        }
        setErrore(e.message);
      } else setErrore(auth.accedi.credenzialiErrate);
    }
  }

  const err = formState.errors;
  return (
    <>
      <PageHeading subtitle={t.sottotitolo}>{t.titolo}</PageHeading>
      {percorsoPubblico.data && (
        <Alert variant={iscrizioneAperta ? 'info' : 'warning'} title={percorsi.registrazionePercorso(percorsoPubblico.data.titolo)}>
          {iscrizioneAperta ? null : percorsi.registrazionePercorsoChiusa}
        </Alert>
      )}
      <form onSubmit={handleSubmit(invia)} noValidate className="flex flex-col gap-6">
        {errore && <Alert variant="error" title={errore} />}
        <Controller
          control={control}
          name="codiceFiscale"
          render={({ field }) => (
            <CodiceFiscaleField
              label={auth.campi.codiceFiscale}
              value={field.value}
              onChange={(v) => field.onChange(v)}
              onBlur={field.onBlur}
              error={err.codiceFiscale?.message}
              autoComplete="off"
            />
          )}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <TextField label={auth.campi.nome} autoComplete="given-name" error={err.nome?.message} {...register('nome')} />
          <TextField label={auth.campi.cognome} autoComplete="family-name" error={err.cognome?.message} {...register('cognome')} />
        </div>
        <TextField label={auth.campi.email} type="email" autoComplete="email" error={err.email?.message} {...register('email')} />
        <TextField label={auth.campi.ordine} hint={auth.campi.aiutoOrdine} autoComplete="organization" error={err.ordine?.message} {...register('ordine')} />
        <TextField label={auth.campi.numeroAlbo} hint={auth.campi.aiutoNumeroAlbo} error={err.numeroAlbo?.message} {...register('numeroAlbo')} />
        <p className="t-body-small text-ink-muted">{t.privacy}</p>
        <Button type="submit" variant="primary" size="lg" fullWidth loading={registrazione.isPending} loadingText={t.inCorso}>
          {t.azione}
        </Button>
      </form>
      <p className="t-body text-ink-muted">
        {t.haiAccount} <Link to="/accedi">{t.accedi}</Link>
      </p>
    </>
  );
}
